/**
 * The menu as the app names it: the side menu's modules and the tabs inside each module. Layout shows these names in the page
 * title; the help search and the help assistant's text (assistant/knowledgeText.js) use them to say where a screen is. A new
 * tab needs its name here as well as in its module's section list.
 */
export const MODULE_LABELS = {
  '/dashboard': 'Dashboard',
  '/account-management': 'Account Management',
  '/affiliate': 'Affiliate',
  '/sales': 'Sales',
  '/store': 'Online Store',
  '/purchase': 'Purchase',
  '/inventory': 'Inventory',
  '/quality': 'Quality',
  '/production': 'Production',
  '/accounts': 'Accounts',
  '/org-admin': 'Org Admin',
  '/tech-support': 'Tech Support',
  '/system-admin': 'System Admin',
  '/settings': 'Settings',
};

/** Tab name by module address and section key (the section is the last part of /<module>/<section>). */
export const TAB_NAMES = {
  '/help': {
    'support': 'Ask & contact',
    'my-requests': 'My requests',
    'getting-started': 'Getting started',
    'walkthroughs': 'Walkthroughs'
  },
  '/production': {
    'boms': 'BOM',
    'work-orders': 'Work Orders',
    'job-work': 'Job Work'
  },
  '/sales': {
    'customers': 'Customers',
    'quotations': 'Quotations',
    'orders': 'Orders',
    'catalog': 'Product Catalog',
    'price-lists': 'Price Lists',
    'salebills': 'Sale Bills',
    'notes': 'Credit / Debit Notes',
    'reports': 'Reports'
  },
  '/purchase': {
    'vendor-types': 'Vendor Types',
    'vendors': 'Vendors',
    'procurement-surveys': 'Procurement Surveys',
    'purchase-orders': 'Purchase Orders',
    'weigh-bridge-receipts': 'WBR',
    'purchase-invoices': 'Purchase Invoices',
    'non-gst-purchases': 'Non-GST Purchases',
    'reports': 'Reports'
  },
  '/inventory': {
    'stock-levels': 'Stock Levels',
    'stock-transfer-orders': 'Stock Transfer Orders',
    'grns': 'Goods Receipt Notes',
    'gins': 'Goods Issue Notes',
    'stock-adjustments': 'Stock Adjustments',
    'stock-ageing': 'Stock Ageing'
  },
  '/accounts': {
    'ledgers': 'Ledgers',
    'bank-accounts': 'Bank Accounts',
    'bank-reconciliation': 'Bank Reconciliation',
    'cash-in-hand': 'Cash-in-Hand',
    'payables': 'Payables',
    'vendor-payments': 'Vendor Payments',
    'expenses': 'Expenses',
    'transfers': 'Transfers & Journals',
    'receivables': 'Receivables',
    'gst-returns': 'GST Returns',
    'financial-statements': 'Financial Statements',
    'opening-balances': 'Opening Balances',
    'import-export': 'Import / Export',
    tds: 'TDS',
    'edit-log': 'Edit Log'
  },
  '/store': {
    'orders': 'Orders',
    'products': 'Products online',
    'reports': 'Reports'
  },
  '/org-admin': {
    'overview': 'Overview',
    'license': 'License',
    'users': 'Users',
    'roles': 'Roles',
    'retail-stores': 'Retail Stores',
    'warehouses': 'Warehouses',
    'production-units': 'Production Units',
    'organization-materials': 'Materials',
    'organization-services': 'Services',
    'quality-grades': 'Quality Grades',
    'products': 'Products',
    'online-store': 'Online Store',
    'import-export': 'Import / Export',
    'edit-log': 'Edit Log',
    'settings': 'Org settings'
  },
  '/account-management': {
    'plans': 'Plans & Pricing',
    'compare': 'Compare',
    'organizations': 'Organizations',
    'tickets': 'Support Tickets',
    'affiliates': 'Affiliates',
    'guides': 'Sales Guides',
    'assistant': 'Search & Ask'
  },
  '/system-admin': {
    'dashboard': 'Dashboard',
    'organizations': 'Organizations',
    'system-admins': 'System Admins',
    'uom': 'Units of Measurement',
    'goods-movement-types': 'Goods Movement Types',
    'tax-rates': 'Tax Rates',
    'subscription-plans': 'Subscription Plans',
    'partners': 'Account Managers',
    'partner-applications': 'Account Manager Applications',
    'affiliates': 'Affiliates',
    'partner-commissions': 'Partner Commissions',
    'subscription-plan-requests': 'Plan Requests',
    'ledger-categories': 'Ledger Categories',
    'email': 'Email',
    'announcements': 'Announcements',
    'email-broadcast': 'Broadcast Email',
    'media': 'Media'
  },
  '/tech-support': {
    'dashboard': 'Dashboard',
    'organizations': 'Organizations',
    'feedback': 'Feedback',
    'assistant-questions': 'Assistant Questions',
    'shop-directory': 'Shop Directory',
    'material-master': 'Material Master',
    'services-master': 'Services Master'
  }
};

// A section's help topic is named like the section, apart from these (and Account Management's, which start with partner-, and
// System Admin's, which start with sysadmin-)
const HELP_TOPIC_OVERRIDES = {
  '/production/boms': 'bom',
  '/sales/catalog': 'product-catalog',
  '/store/products': 'online-store',
  '/store/orders': 'online-store-orders',
  '/store/reports': 'online-store-reports',
  '/sales/notes': 'sale-notes',
  '/sales/reports': 'sales-reports',
  '/purchase/reports': 'purchase-reports'
};

/** The help topic key of a section: helpTopicKey('/accounts', 'payables') is 'payables'. Not every section has a topic. */
export function helpTopicKey(modulePath, section) {
  const override = HELP_TOPIC_OVERRIDES[`${modulePath}/${section}`];
  if (override) return override;
  if (modulePath === '/account-management') return `partner-${section}`;
  if (modulePath === '/system-admin' || modulePath === '/tech-support') return section === 'material-master' ? 'material-master' : `sysadmin-${section}`;
  return section;
}

/**
 * Topics for System Admin's own screens: internal to iBigDo, so they are left out of the help search and of what the help
 * assistant answers from (neither customers nor account managers have these screens).
 */
export function isInternalTopic(key) {
  return key.startsWith('sysadmin-') || key === 'material-master';
}
